"use client";

import { useEffect, useRef } from "react";
import {
  decodeJwtClaims,
  extractPermissions,
  extractRoles,
  extractTenantIds,
} from "./claims";
import { useAuthStore, type SessionState } from "./auth-store";
import type { JwtClaims } from "./claims";
import { useClarityAuth } from "./use-clarity-auth";
import {
  handleSessionExpired,
  isReauthRequiredError,
  resetSessionExpiredGuard,
} from "./session-expiry";

const loadingState: SessionState = {
  status: "loading",
  accessToken: null,
  claims: null,
  tenantIds: [],
  roles: [],
  permissions: [],
  error: null,
};

const signedOutState: SessionState = {
  status: "signed_out",
  accessToken: null,
  claims: null,
  tenantIds: [],
  roles: [],
  permissions: [],
  error: null,
};

function toAuth0UserClaims(value: unknown): JwtClaims {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  const claims = { ...(value as JwtClaims) };
  delete claims.__raw;

  return claims;
}

export function resolveSessionClaims(accessToken: string, user: unknown) {
  const userClaims = toAuth0UserClaims(user);

  try {
    return {
      ...userClaims,
      ...decodeJwtClaims(accessToken),
    };
  } catch (error) {
    if (
      extractTenantIds(userClaims).length > 0 ||
      extractRoles(userClaims).length > 0 ||
      extractPermissions(userClaims).length > 0
    ) {
      return userClaims;
    }

    throw error;
  }
}

export function useInitializeSession() {
  const {
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
    isAuthenticated,
    isLoading,
    user,
  } = useClarityAuth();
  const setSessionState = useAuthStore((s) => s.setSessionState);
  const setGetAccessTokenSilently = useAuthStore(
    (s) => s.setGetAccessTokenSilently,
  );
  const setLoginWithRedirect = useAuthStore((s) => s.setLoginWithRedirect);
  const setLogout = useAuthStore((s) => s.setLogout);
  const hasAuthenticatedRef = useRef(false);

  useEffect(() => {
    setGetAccessTokenSilently(getAccessTokenSilently);
  }, [getAccessTokenSilently, setGetAccessTokenSilently]);

  useEffect(() => {
    setLoginWithRedirect(loginWithRedirect);
  }, [loginWithRedirect, setLoginWithRedirect]);

  useEffect(() => {
    setLogout(logout);
  }, [logout, setLogout]);

  useEffect(() => {
    let isCancelled = false;

    if (isLoading) {
      if (!hasAuthenticatedRef.current) {
        queueMicrotask(() => {
          if (!isCancelled) setSessionState(loadingState);
        });
      }
      return () => {
        isCancelled = true;
      };
    }

    if (!isAuthenticated) {
      hasAuthenticatedRef.current = false;
      queueMicrotask(() => {
        if (!isCancelled) setSessionState(signedOutState);
      });
      return () => {
        isCancelled = true;
      };
    }

    async function loadSession() {
      // Only show the full loading gate for the initial sign-in. Later
      // re-runs (background token refresh) happen while already
      // authenticated, so keep rendering the current session in place
      // instead of unmounting the authenticated app tree.
      if (!hasAuthenticatedRef.current) {
        queueMicrotask(() => {
          if (!isCancelled) setSessionState(loadingState);
        });
      }

      try {
        const accessToken = await getAccessTokenSilently();
        const claims = resolveSessionClaims(accessToken, user);

        if (isCancelled) return;

        hasAuthenticatedRef.current = true;
        resetSessionExpiredGuard();
        setSessionState({
          status: "authenticated",
          accessToken,
          claims,
          tenantIds: extractTenantIds(claims),
          roles: extractRoles(claims),
          permissions: extractPermissions(claims),
          error: null,
        });
      } catch (error) {
        if (isCancelled) return;

        if (isReauthRequiredError(error)) {
          handleSessionExpired();
          return;
        }

        setSessionState({
          status: "error",
          accessToken: null,
          claims: null,
          tenantIds: [],
          roles: [],
          permissions: [],
          error:
            error instanceof Error
              ? error.message
              : "The ClarityOS session could not be initialized.",
        });
      }
    }

    void loadSession();

    return () => {
      isCancelled = true;
    };
  }, [
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    setSessionState,
    user,
  ]);
}
